import { Button, Flex, Group, Switch, Text } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  deleteEmployee,
  getEmployeeList,
  restoreEmployee,
} from "../../api/employeeApi";
import { deleteNotification } from "../../utils/useNotification";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import TableEditButton from "../common/Buttons/TableEditButton";
import styled, { keyframes, css } from "styled-components";
import TableInfoButton from "../common/Buttons/TableInfoButton";
import CustomSelect from "../common/Inputs/CustomSelect";
import TagBadges from "../common/TagBadges";
import DataTable, { Column } from "../common/Table/DataTable";
import { useTableQuery } from "../../hooks/useTableQuery";
import { useDeleteWithConfirm } from "../../hooks/useDeleteWithConfirm";
import { useAuth } from "../../context/AuthContext";

const EmployeesTable = () => {
  const { tagSelectData, departmentSelectData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showDeleted, setShowDeleted] = useState<boolean>(
    () => localStorage.getItem("employees.showDeleted") === "true"
  );

  const table = useTableQuery({ fetcher: getEmployeeList });
  const { setFilter } = table;

  useEffect(() => {
    setFilter("includeDeleted", showDeleted ? true : undefined);
  }, [showDeleted, setFilter]);

  // Aktif personel pasife alınır, zaten pasif olan kalıcı silinir — onay metni buna göre değişir.
  const { askDelete, modal } = useDeleteWithConfirm({
    deleteFn: deleteEmployee,
    onDeleted: table.refetch,
    title: (target) =>
      target?.isWorkingCurrently === false
        ? "Kalıcı Silme Onayı"
        : "Pasife Alma Onayı",
    text: (target) =>
      target?.isWorkingCurrently === false
        ? "Bu personel listeden kalıcı olarak kaldırılacak."
        : "Bu personel pasife alınacak. Listede pasif olarak görünmeye devam edecek.",
    confirmButtonText: "Onayla",
  });

  const handleRestoreClick = useCallback(
    async (id: string | null) => {
      if (!id) return;
      try {
        const { data }: any = await restoreEmployee({ id });
        deleteNotification(data?.success, "Personel geri yüklendi.");
        table.refetch();
      } catch (error) {
        console.error(error);
      }
    },
    [table]
  );

  const columns: Column[] = [
    {
      key: "name",
      header: `Ad Soyad ${table.sort === "asc" ? "↑" : "↓"}`,
      width: "18%",
      onHeaderClick: () => table.setSort(table.sort === "asc" ? "desc" : "asc"),
      render: (row) => (
        <Group gap="sm" wrap="nowrap">
          <SimpleAvatar>{row.name ? row.name[0] : "?"}</SimpleAvatar>
          <Text fw={400} size="md" style={{ color: "#111827" }}>
            {row.name}
          </Text>
        </Group>
      ),
    },
    {
      key: "phone",
      header: "Telefon",
      width: "16%",
      render: (row) => (
        <Text size="md" fw={400} style={{ color: "#111827" }}>
          {row.phone || "—"}
        </Text>
      ),
    },
    {
      key: "departments",
      header: "Departman",
      width: "17%",
      render: (row) => (
        <TagBadges
          max={2}
          tags={(row.departments ?? []).map((d: any) => ({
            ...d,
            kind: "department",
          }))}
        />
      ),
    },
    {
      key: "tags",
      header: "Rol / Yetenek",
      width: "17%",
      // Tek kaynak `tags`. Eski `roles` alanına geri düşüş KALDIRILDI: arka uç
      // artık o alanı hiç döndürmüyor, rol ve yetenek `tagIds` içinde.
      render: (row) => <TagBadges max={2} tags={row.tags ?? []} />,
    },
    {
      key: "status",
      header: "Durum",
      width: "14%",
      render: (row) => {
        if (row.isDeleted) return <DeletedPill>Silinmiş</DeletedPill>;
        const isActive = row.isWorkingCurrently;
        return (
          <StatusPill $isActive={isActive}>
            <StatusDot $isActive={isActive} />
            <span>{isActive ? "Aktif" : "Pasif"}</span>
          </StatusPill>
        );
      },
    },
    {
      key: "actions",
      header: "İşlemler",
      width: "18%",
      headerAlign: "right",
      render: (row) => (
        <Flex justify="flex-end" gap={6} onClick={(e) => e.stopPropagation()}>
          <TableInfoButton
            onClick={() => navigate(`${location.pathname}/${row._id}`)}
          />
          {row.isDeleted ? (
            <Button
              size="xs"
              variant="light"
              color="teal"
              onClick={() => handleRestoreClick(row._id || null)}
            >
              Geri Yükle
            </Button>
          ) : (
            <>
              <TableEditButton
                onClick={() =>
                  navigate(`/employees/${row._id}/edit`, {
                    state: { employee: row },
                  })
                }
              />
              <TableDeleteButton onClick={() => askDelete(row)} />
            </>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={table.rows}
        loading={table.loading}
        emptyText="Personel bulunamadı."
        onRowClick={(row: any) => navigate(`/employees/${row._id}`)}
        rowStyle={(row: any) => (row.isDeleted ? { opacity: 0.6 } : undefined)}
        searchValue={table.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Personel ara..."
        toolbar={
          <>
            <CustomSelect
              placeholder="Departman"
              data={departmentSelectData}
              clearable
              onChange={(value: any) =>
                table.setFilter("department", value ?? undefined)
              }
            />
            <CustomSelect
              placeholder="Rol / Yetenek"
              data={[...tagSelectData.role, ...tagSelectData.skill]}
              clearable
              onChange={(value: any) =>
                table.setFilter("tag", value ?? undefined)
              }
            />
            <CustomSelect
              placeholder="Durum"
              data={[
                { label: "Çalışıyor", value: "active" },
                { label: "Ayrıldı", value: "inactive" },
              ]}
              clearable
              onChange={(value: any) =>
                table.setFilter(
                  "isWorkingCurrently",
                  value === "active" ? true : value === "inactive" ? false : undefined
                )
              }
            />
          </>
        }
        subToolbar={
          <Switch
            checked={showDeleted}
            onChange={(e) => {
              const v = e.currentTarget.checked;
              setShowDeleted(v);
              localStorage.setItem("employees.showDeleted", String(v));
            }}
            label="Silinmiş personeli göster"
            size="sm"
            color="#5C26FF"
            styles={{
              label: { fontSize: 13, color: "#374151", whiteSpace: "nowrap" },
            }}
          />
        }
        page={table.page}
        totalPages={table.totalPages}
        onPageChange={table.setPage}
        limit={table.limit}
        onLimitChange={table.setLimit}
      />
      {modal}
    </>
  );
};

export default EmployeesTable;

const pulseDot = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
  60% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
`;

const SimpleAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #e1e7fc;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  border: 1px solid #d1d9f5;
  flex-shrink: 0;
`;

const StatusPill = styled.div<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  min-width: 80px;
  justify-content: center;

  ${(p) =>
    p.$isActive
      ? css`
          color: #059669;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
        `
      : css`
          color: #dc2626;
          background: #fef2f2;
          border: 1px solid #fecaca;
        `}
`;

const DeletedPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  min-width: 80px;
  justify-content: center;
  color: #6b7280;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`;

const StatusDot = styled.div<{ $isActive: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => (p.$isActive ? "#10B981" : "#ef4444")};
  ${(p) =>
    p.$isActive &&
    css`
      animation: ${pulseDot} 2s infinite;
    `}
`;
